import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ProcessingJobApiService } from './processing-job-api.service';

@Injectable({ providedIn: 'root' })
export class ProcessingRuntimeStatusReporter {
  private readonly api = inject(ProcessingJobApiService);

  /**
   * Per-job throttle. Rapid `processing` progress updates (now that the worker
   * renders fast, and main+shorts report concurrently) used to burst this
   * endpoint and get rate-limited (HTTP 429). We coalesce in-progress updates to
   * at most one per `MIN_INTERVAL_MS` per job and drop a new one while a previous
   * POST is still in flight. Terminal (`completed`/`failed`) and step-output
   * updates ALWAYS go through.
   */
  private readonly state = new Map<string, { lastAt: number; inFlight: boolean }>();
  private static readonly MIN_INTERVAL_MS = 1500;

  async update(
    jobId: string | null | undefined,
    status: 'processing' | 'completed' | 'failed' | string,
    progress: number,
    currentStep: string,
    errorMessage?: string | null,
    stepOutputJson?: unknown
  ): Promise<void> {
    if (!jobId) return;

    const terminal = status === 'completed' || status === 'failed';
    const important = terminal || stepOutputJson != null;
    const st = this.state.get(jobId) ?? { lastAt: 0, inFlight: false };

    if (!important) {
      if (st.inFlight) return; // a POST is already in flight → coalesce
      if (Date.now() - st.lastAt < ProcessingRuntimeStatusReporter.MIN_INTERVAL_MS) return;
    }

    st.inFlight = true;
    st.lastAt = Date.now();
    this.state.set(jobId, st);

    try {
      await firstValueFrom(this.api.updateRuntimeJob(jobId, {
        status,
        progress: Math.max(0, Math.min(100, Math.round(progress))),
        currentStep,
        errorMessage,
        stepOutputJson: stepOutputJson ?? null
      }));
    } catch (err) {
      console.warn('Failed to update runtime processing job status', err);
    } finally {
      st.inFlight = false;
      st.lastAt = Date.now();
      if (terminal) this.state.delete(jobId);
      else this.state.set(jobId, st);
    }
  }
}
